import { fetchAuthSession } from 'aws-amplify/auth'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { RootState } from 'store'
import { SidebarBrandLogo } from 'components/Shell/AppSidebar/SidebarBrandLogo'
import Chevron from 'assets/icons/Chevron'
import useAuth from 'hooks/useAuth'
import { setMobileSidebar } from 'features/global/globalSlice'
import { ILink, linksAdmin, linksClient } from '../../constants/links'
import {
  Backdrop,
  ChevronWrap,
  Drawer,
  DrawerCloseBtn,
  DrawerGroup,
  DrawerHeader,
  DrawerLink,
  DrawerLogo,
  DrawerNav,
  DrawerSubLink,
} from './SidebarMobile.styled'

const SidebarMobile = () => {
  const dispatch = useDispatch()
  const open = useSelector((state: RootState) => state.globals.isMobileSidebarOpen)
  const [expanded, setExpanded] = useState<string | null>(null)
  const { user } = useSelector((state: RootState) => state.user)
  const { isAdmin } = useAuth()
  const [links, setLinks] = useState<ILink[]>([])
  const navigate = useNavigate()
  const location = useLocation()
  const pathname = `/${location.pathname.split('/')[1]}`

  const close = () => dispatch(setMobileSidebar(false))

  useEffect(() => {
    const getLinks = async () => {
      const groups = await fetchAuthSession().then(
        (session) => session.tokens?.idToken?.payload['cognito:groups'],
      )
      if (isAdmin) {
        setLinks(linksAdmin)
      } else if ((groups as string[])?.includes('company_administrator')) {
        setLinks(linksClient)
      } else {
        setLinks(
          linksClient.filter(
            (l) =>
              l.abr === '*' ||
              l.abr === 'operationGroups' ||
              l.abr === 'tasks' ||
              l.abr === 'configurations' ||
              l.abr === 'ops' ||
              l.abr === 'organogram' ||
              user?.currentContract?.role?.permissionPolicy[l.abr],
          ),
        )
      }
    }
    getLinks()
  }, [isAdmin, user])

  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open])

  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden'
    else document.body.style.overflow = ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  const handleNavigate = (path: string) => {
    const target = path.startsWith('/') ? path : `/${path}`
    navigate(target)
    close()
  }

  const isItemActive = (item: ILink) =>
    pathname === item.path ||
    pathname.includes(item.path.split('-')[0]) ||
    (pathname === '/' && item.text === 'employees')

  return (
    <>
      <Backdrop $open={open} onClick={close} />

      <Drawer $open={open}>
        <DrawerHeader>
          <DrawerLogo>
            <SidebarBrandLogo />
          </DrawerLogo>
          <DrawerCloseBtn onClick={close} aria-label='Close menu'>
            <svg viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path
                d='M6 6L18 18M18 6L6 18'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </DrawerCloseBtn>
        </DrawerHeader>

        <DrawerNav>
          {links.map((item) => {
            const active = isItemActive(item)
            const isExpanded = expanded === item.text
            return (
              <DrawerGroup key={item.text + item.path}>
                <DrawerLink
                  $active={active}
                  onClick={() => {
                    if (item.list) {
                      setExpanded(isExpanded ? null : item.text)
                    } else {
                      handleNavigate(item.path)
                    }
                  }}
                >
                  <span className='icon-wrap'>{item.icon}</span>
                  <span className='label'>{item.text}</span>
                  {item.list && (
                    <ChevronWrap $open={isExpanded} className='chev'>
                      <Chevron />
                    </ChevronWrap>
                  )}
                </DrawerLink>

                {item.list &&
                  isExpanded &&
                  item.list.map((sub) => (
                    <DrawerSubLink
                      key={sub.path}
                      $active={location.pathname.includes(sub.path)}
                      onClick={() => handleNavigate(sub.path)}
                    >
                      {sub.name}
                    </DrawerSubLink>
                  ))}
              </DrawerGroup>
            )
          })}
        </DrawerNav>
      </Drawer>
    </>
  )
}

export default SidebarMobile
